import { LogLevel, Configuration, PublicClientApplication } from '@azure/msal-browser';

// **AZURE APP REGISTRATION - FROM ENVIRONMENT VARIABLES**
console.log('=== MSAL CONFIG DEBUG ===');
console.log('REACT_APP_AZURE_CLIENT_ID:', process.env.REACT_APP_AZURE_CLIENT_ID);
console.log('REACT_APP_AZURE_AUTHORITY:', process.env.REACT_APP_AZURE_AUTHORITY);
console.log('REACT_APP_REDIRECT_URI:', process.env.REACT_APP_REDIRECT_URI);
console.log('========================');

const msalConfig: Configuration = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
    authority: process.env.REACT_APP_AZURE_AUTHORITY || '',
    redirectUri: process.env.REACT_APP_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: process.env.REACT_APP_REDIRECT_URI || window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
    claimsBasedCachingEnabled: true,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
      logLevel: process.env.NODE_ENV === 'development' ? LogLevel.Verbose : LogLevel.Error,
    },
  },
};

// **CLEAR OLD CACHE WHEN CONFIG CHANGES**
if (typeof window !== 'undefined') {
  const currentAuthority = process.env.REACT_APP_AZURE_AUTHORITY;
  const cachedAuthority = sessionStorage.getItem('msal.config.authority');
  
  if (cachedAuthority && cachedAuthority !== currentAuthority) {
    console.log('Authority changed, clearing MSAL cache...');
    sessionStorage.clear();
    localStorage.removeItem('msal.account.keys');
    localStorage.removeItem('msal.token.keys');
  }
  
  if (currentAuthority) {
    sessionStorage.setItem('msal.config.authority', currentAuthority);
  }
}

// **MSAL INSTANCE**
export const msalInstance = new PublicClientApplication(msalConfig);

// **LOGIN REQUEST SCOPES - MIT GRUPPENBERECHTIGUNG**
export const loginRequest = {
  scopes: ['User.Read', 'GroupMember.Read.All'], // Basis + Gruppenmitgliedschaften
  prompt: 'select_account'
};

// **GRAPH API CONFIGURATION**
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  graphUserPhotoEndpoint: 'https://graph.microsoft.com/v1.0/me/photo/$value',
};

// **INITIALIZE MSAL - UPDATED FOR APP ROLES**
export const initializeMsal = async () => {
  try {
    await msalInstance.initialize();
    console.log('✅ MSAL initialized successfully');
    
    // Handle redirect flows (after login)
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      console.log('✅ Redirect login successful:', response.account?.username);
    }
    
    return msalInstance;
  } catch (error) {
    console.error('❌ MSAL initialization failed:', error);
    throw error;
  }
};

// **SAFE TOKEN ACQUISITION - NO GROUPS, JUST USER INFO**
export const safeAcquireTokenSilent = async () => {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error('No accounts found');
    }

    const request = {
      ...loginRequest,
      account: accounts[0],
    };

    const response = await msalInstance.acquireTokenSilent(request);
    return response;
  } catch (error) {
    console.error('Silent token acquisition failed:', error);
    throw error;
  }
};

// **SAFE GET ALL ACCOUNTS**
export const safeGetAllAccounts = () => {
  try {
    return msalInstance.getAllAccounts();
  } catch (error) {
    console.error('Error getting accounts:', error);
    return [];
  }
};

// **API REQUEST HELPER - UPDATED FOR APP ROLES**
export const apiRequest = async (url: string, options: RequestInit = {}) => {
  try {
    const accounts = safeGetAllAccounts();
    if (accounts.length === 0) {
      throw new Error('User not authenticated');
    }

    const tokenResponse = await safeAcquireTokenSilent();
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tokenResponse.accessToken}`,
    };

    const response = await fetch(url, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// **ENTRA ID GROUPS - EINZIGE AUTORISIERUNGSMETHODE**
export const ENTRA_ID_GROUPS = {
  // Admin und Hauptfunktionen
  ADMIN: 'supplierportal_admin',
  SUPPLIERS: 'supplierportal_suppliers',
  ANALYSES: 'supplierportal_analyses',
  
  // EK Bereiche
  EK1: 'supplierportal_EK1',
  EK2: 'supplierportal_EK2',
  EK3: 'supplierportal_EK3',
  EK4: 'supplierportal_EK4',
  EK5: 'supplierportal_EK5',
  EK6: 'supplierportal_EK6',
  EK7: 'supplierportal_EK7',
  EK8: 'supplierportal_EK8',
  EK9: 'supplierportal_EK9',
  EKV: 'supplierportal_EKV',
  EKW: 'supplierportal_EKW',
  EKG: 'supplierportal_EKG',
  EKD: 'supplierportal_EKD',
  EKSG: 'supplierportal_EKSG'
} as const;

// **GET USER GROUPS VIA MICROSOFT GRAPH**
export const getUserGroups = async () => {
  try {
    // Nutze die korrekte Berechtigung für Gruppenmitgliedschaften
    const response = await apiRequest('https://graph.microsoft.com/v1.0/me/memberOf?$select=displayName,id');
    const data = await response.json();
    
    const groups = data.value
      .filter((item: any) => item['@odata.type'] === '#microsoft.graph.group')
      .map((group: any) => group.displayName)
      .filter((groupName: string) => groupName && groupName.startsWith('supplierportal_')); // Nur relevante Gruppen
    
    console.log('[AUTH] User Entra ID Groups:', groups);
    return groups;
  } catch (error) {
    console.error('[AUTH] Failed to fetch user groups:', error);
    console.warn('[AUTH] Fallback: Access denied without group membership');
    return [];
  }
};

// **EXPORT DEFAULT CONFIG**
export default msalConfig;